import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { RootState } from '../reducers/reducers';
import { refreshAuthCredentials } from '../api';
import { isTokenRefreshDue } from '../utils/tokenExpiry';

export interface BaseAPIResponse<T> {
    data: T;
    errorMessage?: string;
    success: boolean;
}

const authedBaseQuery = fetchBaseQuery({
    baseUrl: '/',
    prepareHeaders: (headers, { getState }) => {
        const state = getState() as RootState;
        const auth = state.auth?.authCredentials;
        if (auth?.token) {
            headers.set('authorization', `Bearer ${auth.token}`);
        }
        return headers;
    },
});

export const baseQuery: typeof authedBaseQuery = async (args, api, extraOptions) => {
    const result = await refreshingBaseQuery(args, api, extraOptions);

    // Backend send errors as 200 OK, so RTK would otherwise treat them as data
    const body = result.data as { type?: string; message?: string } | undefined;
    if (body?.type === 'error') {
        return { error: { status: 'CUSTOM_ERROR', error: body.message ?? '', data: body }, meta: result.meta };
    }

    return result;
};

// The access token only lives 15 minutes, so refresh once when a due token gets a 401 and replay. The
// refresh writes the new credentials back to the store, which prepareHeaders reads on the retry.
const refreshingBaseQuery: typeof authedBaseQuery = async (args, api, extraOptions) => {
    const auth = (api.getState() as RootState).auth?.authCredentials;
    const result = await authedBaseQuery(args, api, extraOptions);

    if (!isUnauthorizedError(result.error) || !auth || !isTokenRefreshDue(auth.expiry)) {
        return result;
    }

    try {
        await refreshAuthCredentials();
    } catch {
        return result;
    }

    return authedBaseQuery(args, api, extraOptions);
};

const isUnauthorizedError = (error?: FetchBaseQueryError) =>
    error?.status === 401 || (error?.status === 'PARSING_ERROR' && error.originalStatus === 401);
